// Optional text input shown alongside voice controls during an interactive session
// (chat_settings.interactive_text_input_enabled) — lets the user type a message straight to the
// live realtime agent without ending the voice session, so text and voice can be used together.
import { useState } from 'react';
import { SendIcon } from '@/components/chat/ChatIcons';

export const InteractiveTextInput = ({
  onSend,
  placeholder = 'Type a message to SAM…',
  // Callers that already sit inside a horizontally-padded container (e.g. HandsFreeView) pass ''
  // to avoid doubling up the inset; the default matches VoicePills' own px-4 pb-2.
  wrapperClassName = 'px-4 pb-2',
  // When nested inside another bordered surface (VoiceComposer's combined card), drop the input's
  // own border/background so it sits flush instead of boxing inside a box.
  bare = false,
}: {
  /** Return false when the text was not sent (e.g. reconnecting) to keep it in the box. */
  onSend: (text: string) => boolean | void;
  placeholder?: string;
  wrapperClassName?: string;
  bare?: boolean;
}) => {
  const [value, setValue] = useState('');

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (onSend(trimmed) === false) return;
    setValue('');
  };

  return (
    <div className={`flex items-center gap-2 ${wrapperClassName}`}>
      <div className={`flex-1 flex items-center gap-2 ${bare ? '' : 'bg-ui-subtle border border-ui-border rounded-2xl px-3 py-1.5'}`}>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder}
          className="flex-1 outline-none text-sm bg-transparent text-ui-text placeholder:text-ui-text3"
        />
        <button
          aria-label="Send"
          onClick={submit}
          disabled={!value.trim()}
          className="w-7 h-7 rounded-full btn-sheen flex items-center justify-center text-on-primary disabled:opacity-40 transition flex-shrink-0"
        >
          <SendIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
